/**
 * ReferenceTab Component
 *
 * Contacts, document registry, and contract details in a clean layout.
 */
import React from 'react';
import {
  Cancel as MissingIcon,
  CloudDownload as DownloadIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { displayCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';

import { calculateNextExpirationDate } from '../hooks/useContractAnalytics';
import type { PayerContract } from '../types';

import { CitationChip } from './CitationChip';

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const date = parseCalendarDate(d);
  return date ? displayCalendarDate(date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : d;
}

// ─── Contact Value Row ───────────────────────────────────────────────────────

const ContactRow: React.FC<{ label: string; value: string | null | undefined; copyable?: boolean; isLink?: boolean }> = ({
  label, value, copyable = false, isLink = false,
}) => {
  if (!value) return null;
  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell sx={{ color: 'text.secondary', fontWeight: 500, py: 1, width: '40%', border: 'none', borderBottom: '1px solid', borderBottomColor: 'divider', verticalAlign: 'top' }}>
        {label}
      </TableCell>
      <TableCell sx={{ py: 1, border: 'none', borderBottom: '1px solid', borderBottomColor: 'divider', verticalAlign: 'top' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isLink ? (
            <Link href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontFamily: copyable ? 'monospace' : 'inherit' }}>{value}</Typography>
          )}
          {copyable && (
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => copyToClipboard(value)} sx={{ p: 0.25 }}>
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

// ─── Contacts Section ────────────────────────────────────────────────────────

const ContactsSection: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const contacts = payer.payer.contacts || {};

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}>
        Contacts & IDs
      </Typography>
      <Table size="small">
        <TableBody>
          <ContactRow label="EDI Payer ID" value={payer.payer.payer_id_edi} copyable />
          <ContactRow label="Portal" value={contacts.portal_url} isLink />
          <ContactRow label="Provider Relations" value={contacts.provider_relations_phone} copyable />
          <ContactRow label="Provider Services" value={contacts.provider_services_phone} copyable />
          <ContactRow label="Authorization" value={contacts.auth_phone} copyable />
          <ContactRow label="Claims Address" value={contacts.claims_address} copyable />
          <ContactRow label="Appeals Address" value={contacts.appeals_address} copyable />
          <ContactRow label="Formal Notices" value={contacts.notice_address} copyable />
          <ContactRow label="Fax" value={contacts.provider_relations_fax} copyable />
        </TableBody>
      </Table>
    </Box>
  );
};

// ─── Document Registry Section ───────────────────────────────────────────────

const DocumentsSection: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  const registry = payer.document_registry || {};
  const docs = Object.entries(registry).map(([id, entry]) => ({ id, ...entry }));

  if (docs.length === 0) return null;

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}>
        Source Documents ({docs.length})
      </Typography>
      <Table size="small">
        <TableBody>
          {docs.map((doc) => (
            <TableRow key={doc.id} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: theme.palette.action.hover } }}>
              <TableCell sx={{ py: 1, border: 'none', borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center' }}>
                  {doc.document_type && <Chip label={doc.document_type.replace(/_/g, ' ')} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                  {doc.page_count && <Typography variant="caption" color="text.secondary">{doc.page_count}p</Typography>}
                  {doc.effective_date && <Typography variant="caption" color="text.secondary">{formatDate(doc.effective_date)}</Typography>}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ py: 1, border: 'none', borderBottom: '1px solid', borderBottomColor: 'divider', width: 120 }}>
                {doc.source_url ? (
                  <Button size="small" startIcon={<DownloadIcon />} href={doc.source_url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                    View
                  </Button>
                ) : doc.s3_uri ? (
                  <Button size="small" startIcon={<DownloadIcon />} disabled sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                    PDF
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ─── Contract Details Section ────────────────────────────────────────────────

const ContractDetailsSection: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  const c = payer.contract;
  const exp = calculateNextExpirationDate(c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism);

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}>
        Contract Details
      </Typography>
      <Table size="small">
        <TableBody>
          <ContactRow label="Agreement Type" value={c.agreement_type} />
          <ContactRow label="Effective Date" value={formatDate(c.effective_date)} />
          <ContactRow label="Next Renewal" value={exp ? formatDate(exp) : '\u2014'} />
          <ContactRow label="Initial Term" value={`${c.initial_term_years} year${c.initial_term_years !== 1 ? 's' : ''}`} />
          <ContactRow label="Renewal Mechanism" value={c.renewal_mechanism.replace(/_/g, ' ')} />
          <ContactRow label="Termination Notice" value={c.termination_without_cause_notice_days ? `${c.termination_without_cause_notice_days} days` : undefined} />
          <ContactRow label="Provider" value={c.provider_entity?.legal_name} />
          <ContactRow label="Provider DBA" value={c.provider_entity?.dba} />
          <ContactRow label="Provider ID" value={c.provider_entity?.provider_id} copyable />
        </TableBody>
      </Table>

      {/* Amendments */}
      {c.amendments && c.amendments.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Amendments ({c.amendments.length})
          </Typography>
          {c.amendments.map((a, idx) => (
            <Box key={idx} sx={{ py: 1, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(a.date)}</Typography>
                {a._source && <CitationChip citation={Array.isArray(a._source) ? a._source[0] : a._source} documentRegistry={payer.document_registry} />}
              </Box>
              <Typography variant="body2" color="text.secondary">{a.what_changed || a.description}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Missing Documents */}
      {c.missing_documents && c.missing_documents.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
            Missing Documents
          </Typography>
          {c.missing_documents.map((doc, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <MissingIcon sx={{ fontSize: 16, color: 'error.main' }} />
              <Typography variant="body2" color="error.main">{doc}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Main Export ─────────────────────────────────────────────────────────────

interface ReferenceTabProps {
  payer: PayerContract;
}

export const ReferenceTab: React.FC<ReferenceTabProps> = ({ payer }) => {
  return (
    <Box>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContactsSection payer={payer} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DocumentsSection payer={payer} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 4 }}>
        <ContractDetailsSection payer={payer} />
      </Box>
    </Box>
  );
};
